import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Eye, EyeOff, Lock, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

export default function RedefinirSenha() {
  const [params]   = useSearchParams();
  const navigate   = useNavigate();
  const token      = params.get('token');

  const [tokenValido,  setTokenValido]  = useState(null); // null=validando, true, false
  const [nomeUsuario,  setNomeUsuario]  = useState('');
  const [senha,        setSenha]        = useState('');
  const [confirmar,    setConfirmar]    = useState('');
  const [verSenha,     setVerSenha]     = useState(false);
  const [salvando,     setSalvando]     = useState(false);
  const [concluido,    setConcluido]    = useState(false);

  // Valida o token ao carregar
  useEffect(() => {
    if (!token) { setTokenValido(false); return; }
    fetch(`${API}/auth/validar-token-reset/${token}`)
      .then(r => r.json())
      .then(d => { setTokenValido(d.valido); setNomeUsuario(d.nome || ''); })
      .catch(() => setTokenValido(false));
  }, [token]);

  const salvar = async (e) => {
    e.preventDefault();
    if (senha.length < 8) return toast.error('Senha deve ter no mínimo 8 caracteres.');
    if (senha !== confirmar) return toast.error('As senhas não coincidem.');

    setSalvando(true);
    try {
      const r = await fetch(`${API}/auth/redefinir-senha`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, senha_nova: senha }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.erro);
      toast.success(d.mensagem);
      setConcluido(true);
      setTimeout(() => navigate('/login'), 3000);
    } catch (err) {
      toast.error(err.message || 'Erro ao redefinir senha.');
    }
    setSalvando(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-acre-dark to-[#1a4a2e] flex items-center justify-center p-4">
      <div className="w-full max-w-sm">
        {/* Logo */}
        <div className="text-center mb-6">
          <Link to="/" className="inline-flex items-center gap-2">
            <span className="text-2xl">🌿</span>
            <span className="font-sora font-bold text-2xl text-white">
              Acre<span className="text-acre-gold">Job</span>
            </span>
          </Link>
        </div>

        <div className="card p-7">

          {/* Validando token */}
          {tokenValido === null && (
            <div className="flex flex-col items-center py-8 gap-3">
              <Loader2 size={28} className="animate-spin text-acre-green"/>
              <p className="text-sm text-gray-500">Validando link...</p>
            </div>
          )}

          {/* Token inválido */}
          {tokenValido === false && (
            <div className="text-center py-4">
              <div className="w-14 h-14 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <AlertCircle size={28} className="text-red-500"/>
              </div>
              <h2 className="font-sora font-bold text-acre-dark text-lg mb-2">Link inválido ou expirado</h2>
              <p className="text-sm text-gray-500 mb-5">
                Este link de redefinição não é mais válido.<br/>
                Os links expiram em 2 horas.
              </p>
              <Link to="/login" className="btn-primary inline-flex items-center gap-2 px-5 py-2.5 text-sm">
                Solicitar novo link
              </Link>
            </div>
          )}

          {/* Concluído */}
          {concluido && (
            <div className="text-center py-4">
              <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle size={28} className="text-green-500"/>
              </div>
              <h2 className="font-sora font-bold text-acre-dark text-lg mb-2">Senha redefinida! ✅</h2>
              <p className="text-sm text-gray-500 mb-1">
                Sua nova senha foi salva com sucesso.
              </p>
              <p className="text-xs text-gray-400">Redirecionando para o login...</p>
            </div>
          )}

          {/* Formulário */}
          {tokenValido === true && !concluido && (
            <>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-acre-green/10 rounded-xl flex items-center justify-center">
                  <Lock size={18} className="text-acre-green"/>
                </div>
                <div>
                  <h2 className="font-sora font-bold text-acre-dark">Nova senha</h2>
                  {nomeUsuario && <p className="text-xs text-gray-400">Olá, {nomeUsuario.split(' ')[0]}!</p>}
                </div>
              </div>

              <form onSubmit={salvar} className="space-y-4">
                <div>
                  <label className="label">Nova senha *</label>
                  <div className="relative">
                    <input
                      className="input pr-10"
                      type={verSenha ? 'text' : 'password'}
                      placeholder="Mínimo 8 caracteres"
                      value={senha}
                      onChange={e => setSenha(e.target.value)}
                      required
                      minLength={8}
                    />
                    <button type="button" onClick={() => setVerSenha(v => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                      {verSenha ? <EyeOff size={15}/> : <Eye size={15}/>}
                    </button>
                  </div>
                  {senha.length > 0 && (
                    <div className="mt-1 flex gap-1">
                      {[
                        { ok: senha.length >= 8,           label: '8+ chars' },
                        { ok: /[A-Z]/.test(senha),         label: 'Maiúscula' },
                        { ok: /[0-9]/.test(senha),         label: 'Número'   },
                      ].map(r => (
                        <span key={r.label} className={`text-xs px-1.5 py-0.5 rounded ${r.ok ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-400'}`}>
                          {r.ok ? '✓' : '○'} {r.label}
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                <div>
                  <label className="label">Confirmar nova senha *</label>
                  <input
                    className={`input ${confirmar && confirmar !== senha ? 'border-red-400' : confirmar && confirmar === senha ? 'border-green-400' : ''}`}
                    type={verSenha ? 'text' : 'password'}
                    placeholder="Repita a nova senha"
                    value={confirmar}
                    onChange={e => setConfirmar(e.target.value)}
                    required
                  />
                  {confirmar && confirmar !== senha && (
                    <p className="text-xs text-red-500 mt-1">As senhas não coincidem</p>
                  )}
                </div>

                <button type="submit" disabled={salvando || senha !== confirmar || senha.length < 8}
                  className="btn-primary w-full justify-center py-3 flex items-center gap-2 disabled:opacity-60">
                  {salvando ? <Loader2 size={15} className="animate-spin"/> : <Lock size={15}/>}
                  Salvar nova senha
                </button>
              </form>
            </>
          )}

          {tokenValido !== null && (
            <div className="text-center mt-4">
              <Link to="/login" className="text-sm text-acre-green hover:underline">← Voltar ao login</Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
